// Catalog of the 2026 objectives and their KPIs.
//
// This is the single source of truth for the Initiatives section: the overview page renders the
// objective/KPI tree from it, and each KPI page pulls its prose from here so the narrative and the
// navigation can never drift apart.
//
// No SQL lives in this app. Each KPI names the stored NexusQuery keys it reads; the SQL for those
// is registered by sql/initiatives/register_initiative_queries.sql and is editable by BI in Query
// Commander at /query-commander/editor?queryKey=<key>. Pages call them via useNexusQuery.
//
// `status`:
//   'live'    — every figure on the page comes from a stored query
//   'partial' — the page renders real data but a material part of the KPI is blocked; see `gaps`
//   'proposed'— definition only, no query yet
//
// KPI definitions are still being negotiated by the exec team. Expect churn here.

export const OBJECTIVES = [
  {
    id: 'obj-1',
    pillarId: 'pillar-3',
    number: 1,
    title: 'Predictable Gross Margin Improvement',
    summary:
      'Make gross margin something we steer rather than something we discover at month end. ' +
      'That means knowing which parts we actively price, catching discretionary discounting as it ' +
      'happens, and holding each inventory bucket to its own margin target.',
  },
  {
    id: 'obj-2',
    pillarId: 'pillar-3',
    number: 2,
    title: 'Better Unit Economics per Transaction',
    summary:
      'Get more out of each transaction rather than chasing more transactions. Sell the parts we ' +
      'manage to the customers who value them most, close a higher share of what we quote, and ' +
      'understand how inventory age shapes both margin and returns.',
  },
];

// Structure follows docs/initiatives/design.md exactly:
//     Pillar > Objective > Initiative > KPI
//
// Note the pillar contains the objectives, not the other way round. Also note the numbering —
// Pillar 3, Initiatives 4 and 5. Pillars 1-2 and Initiatives 1-3 exist elsewhere and are not in
// design.md, so this section is one pillar of a larger plan, not the whole 2026 programme.
//
// design.md defines Actions (dated deliverables) under each initiative; it defines no KPIs. The
// KPI layer here is the instrumentation for those Actions' outputs — see docs/initiatives/
// alignment.md §7, which also records where the three source documents disagree.
export const PILLARS = [
  {
    id: 'pillar-3',
    number: 3,
    name: 'Margin Discipline & Economic Control',
  },
];

export const INITIATIVES = [
  {
    id: 'initiative-4',
    number: 4,
    objectiveId: 'obj-1',
    name: 'Pricing Governance & Discipline',
    description:
      'Establish consistent pricing rules, guardrails, and approval workflows to protect margin.',
  },
  {
    id: 'initiative-5',
    number: 5,
    objectiveId: 'obj-2',
    name: 'Transaction Quality & Decision Review Program',
    description:
      'Improve deal evaluation, reduce leakage, and ensure disciplined commercial decision making.',
  },
];

// Shared caveat — every KPI that splits managed from unmanaged rests on this one provisional
// definition, so it is stated once and referenced rather than reworded per page.
const MANAGED_DEFINITION_CAVEAT =
  'Managed now reads from DW.PartDim.ManagedPart — an authoritative flag maintained in Pentagon ' +
  '(Class A or B, or repaired stock in SV/OH on the shelf), not a derived guess. Two things to ' +
  'know: the rule mixes a deliberate class designation with an incidental fact about what is on ' +
  'the shelf today, so a part can drop off the list simply by selling its last repaired unit; and ' +
  'these queries read managed status as it stands today rather than as it stood at the time of ' +
  'sale, though the SCD2 history now makes the as-of version possible.';

export const KPIS = [
  // ---------------------------------------------------------------- Objective 1
  {
    slug: 'managed-parts-coverage',
    executiveSummary:
      'Managed parts carry the business — roughly $91M of the $111M booked over three years comes ' +
      'from the managed list. But the catalog is badly under-priced: of the ~10,900 managed part ' +
      'numbers, only about 740 (7%) currently carry a live System Sell Price, and the premium ' +
      'Class A and B tiers are the least covered. Nearly all of today’s managed revenue still ' +
      'flows through the ~1,600 parts that do have a current SSP, so the pricing-coverage gap is ' +
      'the headline risk, not the sales volume.',
    objectiveId: 'obj-1',
    initiativeId: 'initiative-4',
    name: 'Managed Parts Coverage',
    shortName: 'Managed Parts',
    tagline: 'What share of our business runs on parts we actively price?',
    status: 'live',
    owner: 'TBD',
    queries: [
      { key: 'initiative-managed-parts-coverage', purpose: 'Managed vs unmanaged split' },
      { key: 'initiative-managed-parts-composition', purpose: 'What is on the managed list, by class' },
      { key: 'initiative-managed-parts-list', purpose: 'The list itself — part, class, reason, priced Y/N' },
      { key: 'initiative-managed-catalog-pricing', purpose: 'Catalog SSP coverage by class (current/expired/no-price)' },
      { key: 'initiative-managed-inventory-exposure', purpose: 'On-hand managed inventory value without a current SSP' },
      { key: 'initiative-managed-by-channel', purpose: 'Managed sales by customer channel × condition × key' },
      { key: 'initiative-managed-velocity', purpose: 'Managed sales disposition by condition + distinct parts sold' },
      { key: 'initiative-managed-cost-share', purpose: 'Booked part cost, managed vs all inventory' },
      { key: 'initiative-managed-stock-readiness', purpose: 'On-shelf readiness of managed parts (AR / SV-OH / nothing)' },
    ],
    question:
      'Of the parts we sell, how many carry a System Sell Price (SSP)? What share of revenue and ' +
      'margin do those "managed" parts represent?',
    definition:
      'Managed parts are the ones that need a System Sell Price, taken from the maintained flag ' +
      'rather than inferred. Coverage then asks a separate question: of those parts, how many ' +
      'actually had a current price when they sold? Expiry follows the standing rule — Class A ' +
      'prices lapse after 90 days, Classes B–D after 180.',
    whyItMatters:
      'Margin is only predictable on parts where we have decided the price in advance. Everything ' +
      'else is priced case by case, which means margin varies with whoever is quoting. Growing ' +
      'managed coverage is the mechanism that makes Objective 1 possible at all.',
    measures: [
      '% of revenue sold against a current SSP',
      '% of revenue sold against an expired SSP — the actionable gap',
      'Margin rate by SSP status',
      'Composition of the managed list by class',
    ],
    targets: ['Coverage target and cadence to be set by the exec team.'],
    dataNotes: [
      'Catalog SSP coverage: only ~6.8% of managed parts carry a current SSP, and ~$11.5M of ' +
        'owned on-hand inventory sits on lapsed pricing. The seed (Class A/B + repaired stock) ' +
        'is a bootstrap toward Ryan\'s 7/21 memo\'s customer-led definition (docs/managed_part/*.docx).',
      'Revenue reads DataMart.SalesInvoiceFactView with the view\'s own ForReportingUse filter.',
      'Managed status comes from DW.PartDim.ManagedPart, sourced from the repurposed Pentagon ' +
        'STOCKEXT.FIELD8 and carried through ETL.vPartDimSrc.',
      'Expiry is evaluated as of the invoice date against the condition-matched priced date, not ' +
        'as of today — so a part shows as expired only if the price was already stale when it sold.',
      'The SSP concept is already implemented in the SalesMonitor schema, but those views are ' +
        'built on sales orders and carry no COGS, so margin here is re-derived against invoices.',
    ],
    gaps: [
      'RESOLVED: the managed list is no longer a guess. It is maintained in Pentagon and carried ' +
        'into the warehouse, so "which parts need an SSP" finally has an authoritative answer ' +
        'instead of three competing derivations that all disagreed with the stated ~300.',
      MANAGED_DEFINITION_CAVEAT,
      'A large block of lines carry a sell price with no priced date, and they behave nothing like ' +
        'the rest — far higher margin. Either a data-quality problem or a separate pricing ' +
        'pathway; worth investigating on its own before it is folded into any headline number.',
      'The list is maintained by a SQL script against Pentagon rather than by a person in the ' +
        'Pentagon UI, so it only reflects reality as of the last time that script was run. It ' +
        'needs an owner and a cadence, or it will quietly go stale.',
    ],
  },
  {
    slug: 'ssp-deviation',
    executiveSummary:
      'Across ~11,300 scoreable managed sale lines, 56% sold below the point-in-time SSP, leaking ' +
      'about $8.9M against list over three years; the average line closed 7.4% under SSP. Measured ' +
      'against the more forgiving DOS-based pricing policy — which sanctions a discount as stock ' +
      'ages — 45% of lines are still under-priced, roughly $6.1M the aging allowance does not ' +
      'explain. The average unit is ~800 days on shelf at sale, so age is real, but the floor ' +
      'comparison shows most of the gap is discretionary discounting, not sanctioned aging.',
    objectiveId: 'obj-1',
    initiativeId: 'initiative-4',
    name: 'SSP Deviation & Margin Leakage',
    shortName: 'SSP Deviation',
    tagline: 'How often do we price below our own rules, and what does it cost?',
    status: 'live',
    owner: 'TBD',
    queries: [
      { key: 'initiative-managed-sales-coverage', purpose: 'Managed PNs sold %, share of all lines/qty' },
      { key: 'initiative-managed-sales-ssp-status', purpose: 'Managed sales qty by at-sale SSP status' },
      { key: 'initiative-ssp-deviation-summary', purpose: 'Headline: below SSP vs below pricing policy, leakage, avg dev' },
      { key: 'initiative-ssp-deviation-bands', purpose: 'Deviation depth distribution (line level)' },
      { key: 'initiative-ssp-deviation-by-condition', purpose: 'Deviation split by AR / SV / OH / NEW' },
      { key: 'initiative-ssp-deviation-trend', purpose: 'Monthly leakage and exception rate' },
      { key: 'initiative-ssp-unscored-lines', purpose: 'Managed lines the metric cannot score' },
    ],
    question:
      'On managed parts — the ones that need an SSP — how often are we selling below it, and what ' +
      'does it cost?',
    definition:
      'The Sales-vs-Sell-Price metric the pricing scorecards use, applied to managed parts and ' +
      'measured against the SSP that was in effect AT THE TIME OF SALE — the DW keeps Type-2 (SCD2) ' +
      'price history, so a 2023 sale is judged against the 2023 price, not today’s. A Pricing ' +
      'Exception is any sold line below the at-sale SSP (any amount, no tolerance band). Margin ' +
      'Leakage is the money that cost; average deviation % is the scorecard-shape number.',
    whyItMatters:
      'Setting an SSP achieves nothing if it is routinely overridden. This is the control KPI for ' +
      'Objective 1 — it converts "we have pricing rules" into "our pricing rules hold." It also ' +
      'distinguishes deliberate, approved exceptions from habitual discounting.',
    measures: [
      'Exception rate: % of scored lines sold below SSP',
      'Margin leakage $, absolute and against the revenue base from managed parts',
      'Deviation depth distribution — how far below, not just how often',
      'Unpriced lines: parts the metric cannot score at all',
    ],
    targets: ['Exception-rate ceiling and leakage budget to be set by the exec team.'],
    dataNotes: [
      'Uses the deviation metric already built into SalesInvoiceFactView, so these numbers agree ' +
        'with Quote Analytics rather than competing with it.',
      'Two independent derivations of leakage — the built-in metric and a PartDim-derived one — ' +
        'agree within about 6%, which is the main reason to trust the figure.',
    ],
    gaps: [
      'No policy yet distinguishes an approved exception from leakage, so authorized discounts ' +
        'currently score as leakage. This is the single biggest caveat on the number.',
      'The view drops lines it cannot score, mostly parts with no sell price at all. Those are ' +
        'excluded from the exception rate and surfaced separately rather than hidden.',
      'DEPENDENCY: Initiative 4 Action 2 (due 8/15) defines pricing policy that varies by customer ' +
        'type, part type, class and age. This metric assumes one SSP per part per condition — once ' +
        'pricing policy becomes multi-dimensional, "below SSP" stops being a single number and this ' +
        'calculation needs rework. Better designed for now than rebuilt later.',
    ],
  },
  {
    slug: 'unmanaged-parts-sales',
    executiveSummary:
      'Unmanaged material is a real, active business — about $20M, or 18% of sales revenue, over ' +
      'three years — but it is a much larger share of activity than of dollars: 39% of sale lines, ' +
      '37% of units, and 60% of the distinct part numbers we sold. In short, we move a long tail ' +
      'of many lower-value unmanaged parts alongside the managed core. The opportunity is not to ' +
      'suppress it but to surface the recurring sellers worth promoting onto the managed list.',
    objectiveId: 'obj-1',
    initiativeId: 'initiative-4',
    name: 'Unmanaged Parts Sales',
    shortName: 'Unmanaged Parts',
    tagline: 'Non-managed inventory — sales, channels, and candidates to bring under management.',
    status: 'live',
    owner: 'TBD',
    queries: [
      { key: 'initiative-unmanaged-vs-managed-summary', purpose: 'Unmanaged revenue/lines/parts as a share of all sales' },
      { key: 'initiative-unmanaged-by-condition', purpose: 'How unmanaged material is sold, by condition' },
      { key: 'initiative-unmanaged-by-channel', purpose: 'Unmanaged revenue by channel — intended pathways' },
      { key: 'initiative-unmanaged-promotion-candidates', purpose: 'Recurring unmanaged parts to consider promoting' },
    ],
    question:
      'How big is the unmanaged business, are we commercializing it well, and which parts should be ' +
      'promoted to the managed list?',
    definition:
      'The mirror of Managed Parts Coverage, with the scorecard inverted. Non-managed material has ' +
      'no SSP obligation, so there is no pricing-deviation lens here. Instead: how big is it and ' +
      'how does its margin compare; where it goes (reseller / repair station / specialty are the ' +
      'intended pathways, not a leak); and which recurring-demand parts are candidates to promote ' +
      'onto the managed list.',
    whyItMatters:
      "Per Ryan's memo, non-managed is not ignored — it's actively commercialized. This page checks " +
      'we are placing it well, and feeds the managed list its next promotions — the mechanism that ' +
      'curates the seeded list toward the customer-led definition.',
    measures: [
      'Unmanaged revenue, lines and distinct parts as a share of all sales',
      'How it is sold — condition mix (AR / SV / OH / NEW)',
      'Where it goes — sales by channel (intended commercial pathways)',
      'Promotion candidates: recurring-demand unmanaged parts',
    ],
    targets: ['Baseline established; improvement goals to follow once the exec team reacts to it.'],
    dataNotes: [
      'Exactly the complement of Managed Parts Coverage — same stored query, other bucket, so the ' +
        'two always sum to total by construction.',
    ],
    gaps: [
      MANAGED_DEFINITION_CAVEAT,
      '"Potential Tracking AR sales" from the objective notes was not clear enough to measure. ' +
        'Needs a sentence from whoever wrote it.',
      'Promotion candidates identified here have to be added in Pentagon — the managed flag is ' +
        'maintained there, so this page can surface candidates but not act on them.',
    ],
  },
  {
    slug: 'repair-expected-vs-actual',
    executiveSummary:
      'With repair pricing now benchmarked at the part-and-condition level, we are paying most ' +
      'high-volume vendors above the market signal, not below. ICON Aerospace — our biggest repair ' +
      'partner at ~1,000 comparable repairs — charges about 26% over benchmark, roughly $301K above ' +
      'what other vendors and RFQ quotes imply for the same work; several smaller shops run hotter ' +
      'per repair (Aero Instruments +70%, AOG Reaction +46%). Turn-time is largely reliable, with ' +
      'most vendors close to their promised dates, so the actionable signal here is price — the ' +
      'conflicting-signals table lists the specific repairs worth a second look.',
    objectiveId: 'obj-1',
    initiativeId: 'initiative-4',
    name: 'Repair Vendor Benchmarking',
    shortName: 'Repair Benchmark',
    tagline: 'Is a repair vendor pricing in line with everyone else on the same part?',
    status: 'live',
    owner: 'TBD',
    queries: [
      { key: 'initiative-repair-vendor-benchmark', purpose: 'Vendor repair cost vs the per-part benchmark' },
      { key: 'initiative-repair-conflicting-signals', purpose: 'Specific repairs far from the benchmark (drill-down)' },
      { key: 'initiative-repair-tat-variance', purpose: 'Turn time / on-time / BER by vendor (secondary)' },
    ],
    question:
      'On the same part, does a repair vendor cost in line with the other signals we have — our ' +
      'paid history with other vendors and the RFQ repair quotes — or is it an outlier?',
    definition:
      'We cannot compare a vendor to its own estimate (none is stored). Instead, at the part & ' +
      'condition level, each vendor\'s actual paid repair cost is benchmarked against the median of ' +
      'all repair-price signals for that part: our paid invoices to other vendors, pooled with the ' +
      'RFQ LineType-04 repair probes. Vendors above the benchmark are expensive, below are cheap; ' +
      'repairs more than 25% off are flagged as conflicting signals. Only parts with ≥2 vendors ' +
      'priced are benchmarkable.',
    whyItMatters:
      'Repair cost hits margin after the sale is committed, so overpaying a vendor is pure margin ' +
      'loss. Comparing every vendor to the market on the same part surfaces who is systematically ' +
      'expensive and the specific repairs worth challenging — at vendor-selection time.',
    measures: [
      'Average deviation from the per-part benchmark, by vendor',
      'High / low outlier repair counts per vendor',
      'Net dollars paid above the benchmark',
      'Conflicting-signal drill-down: vendor cost vs the other-vendor price range',
      'Secondary: turn-time / on-time / BER by vendor',
    ],
    targets: ['Deviation tolerance and a $-impact threshold to be set by the exec team.'],
    dataNotes: [
      'Paid repair cost = DW.PurchaseInvoiceFact, LineType 04/38, unit cost = LineTotal / QtyOrdered ' +
        '(DollarAmount is 0% — do not use). This is what we actually paid, not the repair order price.',
      'RFQ repair probes = DW.RFQFactView, LineType 04 (04 = repair avg, 01 = market sell). Both legs ' +
        'restricted to Condition SV/OH; vendor = Acctno → CustVendDim.',
      'The old "cost is unmeasurable" note referred to InventoryFact.Cost (teardown $0) and the ' +
        'RepairOrderFactView price — not the purchase-invoice cost used here.',
    ],
    gaps: [
      'v1 benchmark is median-of-all-signals, not leave-one-out per vendor — so a vendor is technically ' +
        'in its own comparison pool. With many signals the median barely moves; thin parts can soften a ' +
        'flag. Leave-one-out is the refinement.',
      'The pool mixes paid invoices with RFQ quotes. Quotes can be lowball probes, so a paid cost far ' +
        'above a quote-heavy benchmark may be a scope mismatch, not overpayment — flags are for review.',
      'Comparability is exact part number + condition; interchangeable/alternate parts are not pooled yet.',
    ],
  },
  {
    slug: 'margin-by-inventory-bucket',
    executiveSummary:
      'Margin is highly uneven across inventory buckets. Our own non-BCR stock (NOBCR) is by far ' +
      'the most profitable — $32.7M of revenue at 46% margin — while the two largest revenue ' +
      'buckets, consignment (CONSI, $37.5M) and special/customer stock (SPEC, $24.0M), run thin at ' +
      '~15%. That is expected given who owns the inventory, but it means top-line growth in ' +
      'CONSI/SPEC contributes far less profit than the same dollars sold from owned stock. The ' +
      'mix, not just the total, drives the margin.',
    objectiveId: 'obj-1',
    initiativeId: 'initiative-4',
    name: 'Margin by Inventory Bucket',
    shortName: 'Bucket Margin',
    tagline: 'Each bucket earns differently — hold each to its own target.',
    status: 'live',
    owner: 'TBD',
    queries: [{ key: 'initiative-margin-by-bucket', purpose: 'Revenue and margin per bucket' }],
    question:
      'What margin does each inventory bucket produce, and what improvement goal should each carry?',
    definition:
      'Gross margin measured separately per inventory bucket, so each gets its own baseline and ' +
      'its own improvement target rather than being held to a blended company number.',
    whyItMatters:
      'A single blended margin number hides structurally different economics — consignment carries ' +
      'no capital cost, sourcing carries no inventory risk. Holding them all to one target either ' +
      'lets some coast or punishes others unfairly. The spread here is dramatic enough that ' +
      'per-bucket targets are the only sensible way to set goals.',
    measures: [
      'Gross margin $ and margin % per bucket',
      'Revenue mix across buckets',
      'Attainment against each bucket\'s own improvement goal',
    ],
    targets: ['One improvement goal per bucket, set against that bucket\'s own baseline.'],
    dataNotes: [
      'Bucket comes from VirtualWhgroup, already present on the invoice view — no join needed.',
    ],
    gaps: [
      'MISFILED: this is a buying and inventory-strategy metric, but Initiative 4 covers pricing only. Objective 1 names "disciplined buying" in its scope with no initiative behind it. See docs/initiatives/alignment.md §3.1.',
      'Sourcing is a warehouse-mapping bug, not missing data: the whgroup derivation leaves ' +
        'warehouse 204 blank even though 204 is the Sourcing warehouse. The query recovers it by ' +
        'warehouse code as a stopgap; the real fix belongs in the ETL.',
      '"Purchasing" is not a peer of the other four — it lives on a different axis entirely ' +
        '(a warehouse-group table keyed on different codes). The requested five buckets are really ' +
        'four from one taxonomy plus one from another, and the exec team needs to reconcile that.',
    ],
  },

  // ---------------------------------------------------------------- Objective 2
  {
    // This KPI comes from design.md rather than the KPI email. Initiative 5 Work Action 4 commits
    // to tracking these figures, and nothing was measuring them — see alignment.md §2.1.
    slug: 'transaction-quality',
    executiveSummary:
      'The deal-margin spread is healthy at the top — 2,770 transactions at 40%+ margin generated ' +
      '$12.5M, the bulk of profit — but the tail bleeds. 223 deals closed at negative margin, ' +
      'giving back about $667K, and roughly 1,250 more landed under 10%. Fewer than 3% of ' +
      'transactions are loss-making, but each is a discrete, reviewable pricing decision rather ' +
      'than a systemic problem, so tightening that tail is nearly free margin.',
    objectiveId: 'obj-2',
    initiativeId: 'initiative-5',
    name: 'Transaction Quality',
    shortName: 'Transaction Quality',
    tagline: 'How good is the average deal, and how bad is the worst?',
    status: 'live',
    owner: 'Finance (per design.md)',
    queries: [
      { key: 'initiative-transaction-margin-distribution', purpose: 'Deal margin distribution' },
      { key: 'initiative-transaction-quality-trend', purpose: 'Monthly quality and negative-margin trend' },
    ],
    question:
      'What does the distribution of margin per transaction look like, how many deals fall below ' +
      'the margin threshold, and how many lose money outright?',
    definition:
      'Margin measured per invoice document rather than per line, because a transaction review ' +
      'looks at deals, not line items. Deals are bucketed by margin rate, with negative-margin ' +
      'deals broken out separately.',
    whyItMatters:
      'This is the instrumentation for the monthly transaction review. The review selects ' +
      'high-impact, outlier, and sample transactions — this KPI is how outliers get found instead ' +
      'of nominated from memory. Negative-margin deals in particular are self-selecting review ' +
      'candidates: each one is a decision that went wrong in a way worth understanding.',
    measures: [
      'Distribution of transactions by margin band',
      'Count and $ of negative-margin transactions',
      '% of transactions below the margin threshold',
      'Monthly trend on all of the above',
    ],
    targets: [
      'Margin threshold not yet set — the queries take it as an argument and currently default ' +
        'to 20%. Initiative 5 Work Action 1 should set the real figure.',
    ],
    dataNotes: [
      'Aggregated to invoice document (DocNo). Line-level margin would inflate the transaction ' +
        'count and misstate deal quality on multi-line deals.',
      'Directly serves the metrics named in Initiative 5 Work Action 4.',
    ],
    gaps: [
      'Three of Initiative 5\'s seven committed metrics are still unmeasured here: "repairs ' +
        'meeting ROI" is blocked by the repair cost-data gap, "repeat errors" needs the Decision ' +
        'Rule Log to exist first, and no source has been identified for write-downs.',
      'The margin threshold is a placeholder until Work Action 1 sets it. Every below-threshold ' +
        'figure moves when that number is agreed.',
    ],
  },
  {
    slug: 'quote-to-close',
    executiveSummary:
      'Conversion runs low and steady — month to month only about 6-10% of quoted lines, and a ' +
      'similar share of quoted dollars, turn into booked orders — against very large quoted volume ' +
      'of $40-60M most months. What does close, closes fast: average time from quote to order is ' +
      'just 3-4 days. Part of the low rate is that many quoted lines are pricing probes never meant ' +
      'to convert, so the lever is hit-rate, not speed; small movements in conversion move real ' +
      'revenue.',
    objectiveId: 'obj-2',
    initiativeId: 'initiative-5',
    name: 'Quote to Close',
    shortName: 'Quote to Close',
    tagline: 'How much of what we quote turns into revenue, and how fast?',
    status: 'live',
    owner: 'TBD',
    queries: [{ key: 'initiative-quote-to-close-trend', purpose: 'Monthly conversion and cycle time' }],
    question:
      'What share of quoted lines and quoted dollars convert to orders, and how long does it take?',
    definition:
      'Conversion measured on both lines and dollars, plus cycle time from quote issued to order ' +
      'booked.',
    whyItMatters:
      'Conversion is the clearest unit-economics lever: the cost of producing a quote is roughly ' +
      'fixed, so every point of conversion improvement drops through. It is also the counterweight ' +
      'to SSP Deviation — if we tighten pricing discipline, conversion is where the cost of that ' +
      'discipline would show up first.',
    measures: [
      'Line conversion %: quoted lines that became orders',
      'Dollar conversion %: quoted $ that became booked $',
      'Average days from quote to close',
    ],
    targets: ['Conversion and cycle-time targets to be set by the exec team.'],
    dataNotes: [
      'Joins sales orders back to quotes. Worth knowing: the path documented in the DW docs runs ' +
        'the other direction and is populated on about 1% of quote lines — following it yields a ' +
        'conversion rate near zero. The docs need correcting.',
    ],
    gaps: [
      'Needs an agreed attribution window — a quote closing 120 days later probably should not ' +
        'count the same as one closing in three days.',
      '"Close" here means quote to sales order. An existing reporting view walks all the way to ' +
        'invoice, which is stricter and gives a materially lower number. Exec should pick one.',
    ],
  },
  {
    slug: 'managed-vs-unmanaged-segments',
    executiveSummary:
      'The concern is visible in the data: the single largest destination for SV/OH/New Class A/B ' +
      'parts is resellers and brokers — about $30.7M — more than the $19.2M that goes directly to ' +
      'key end users. Premium inventory that could be placed straight with operators is instead ' +
      'flowing through the broker channel, which resells it (often to those same operators) and ' +
      'keeps the relationship. Margin is nearly flat across channels (~19-24%), so the case for ' +
      'going direct is about owning the customer, not capturing immediate price.',
    objectiveId: 'obj-2',
    initiativeId: 'initiative-5',
    name: 'Premium Parts by Channel',
    shortName: 'Channel Fit',
    tagline: 'Are our best parts going to brokers instead of end users?',
    status: 'live',
    owner: 'TBD',
    queries: [
      { key: 'initiative-managed-by-segment', purpose: 'Class A/B revenue by channel, condition and key-customer status' },
      { key: 'initiative-premium-parts-to-resellers', purpose: 'Drill-down: SV/OH/New A/B parts invoiced to resellers' },
    ],
    question:
      'Where do our Class A and B parts end up? Specifically, how much SV/OH/New-condition premium ' +
      'inventory is going to brokers and distributors rather than to end users and key accounts?',
    definition:
      'Class A/B revenue split three ways: by channel (End User / Repair Station / OEM-Tier 1 / ' +
      'Reseller), by condition (New, OH and SV are sellable; AR is core material), and ' +
      'by whether the buyer is a key account. The headline measure is the share of SV/OH/New A/B ' +
      'revenue landing with resellers.',
    whyItMatters:
      'An SV, OH or New Class A part sold to a broker is a part we did not place with an operator. The ' +
      'broker resells it — often to the very end user we wanted — and takes the relationship and ' +
      'the repeat business with it. ITS wants that inventory reaching key customers and end users ' +
      'directly. This KPI measures whether it does.',
    measures: [
      '% of SV/OH/New Class A/B revenue going to resellers',
      'SV/OH/New A/B revenue by channel',
      'Margin rate by channel — is direct actually worth more?',
      'Key vs non-key customer share of premium inventory',
    ],
    targets: ['Reseller share ceiling for SV/OH/New A/B parts to be set by the exec team.'],
    dataNotes: [
      'Scoped to Class A/B rather than the full managed list. The SV/OH-stock half of the managed ' +
        'rule sweeps in C/D/Z parts that are not the premium inventory this KPI is about.',
      'AR (as-removed) is reported separately, not folded in — selling core material to a reseller ' +
        'is normal business, not a leak.',
      'Channel groups the 24 raw market segments into the four that matter commercially.',
    ],
    gaps: [
      'Margin is nearly flat across channels (roughly 21–25%), so the case for shifting away from ' +
        'resellers cannot rest on realized margin as measured today. Either we are discounting to ' +
        'end users as hard as to brokers, or the value of going direct sits in repeat business and ' +
        'relationship rather than in unit price. Worth settling before targets are set.',
      'The reseller/end-user split treats every broker sale as displaceable. Some are not — a ' +
        'broker may be the only route to a given operator. Needs a view on what share is genuinely ' +
        'winnable.',
    ],
  },
  {
    slug: 'managed-sales-age-vs-returns',
    executiveSummary:
      'Most revenue comes from recently-received stock — $54M of sales on units sold within 60 ' +
      'days of receipt — and, notably, margin does not erode with age, holding ~21-26% across ' +
      'every age band, so old stock is not being fire-sold on price. What climbs with age is ' +
      'returns: the return rate roughly triples, from 1.8% on the newest stock to 4.8% on parts ' +
      'sold after 271+ days on shelf. The aging problem shows up as returns and quality risk, not ' +
      'margin, and the 271+ bucket still moves a lot of volume (4,661 lines), so it is an active ' +
      'tail worth watching.',
    objectiveId: 'obj-2',
    initiativeId: 'initiative-5',
    name: 'Managed Sales by Age vs. Returns',
    shortName: 'Age & Returns',
    tagline: 'Does older stock come back more often?',
    status: 'live',
    owner: 'TBD',
    queries: [{ key: 'initiative-age-vs-returns', purpose: 'Sales, margin and returns by age bucket' }],
    question:
      'What share of managed-part sales falls into each inventory age group, and how does that ' +
      'correlate with returns?',
    definition:
      'Managed-part sales bucketed by inventory age at the time of sale, cross-referenced against ' +
      'the return rate for the same buckets.',
    whyItMatters:
      'Aged inventory is usually discussed purely as a carrying-cost problem. If older stock also ' +
      'returns more often, the true cost is materially higher than carrying cost alone — and the ' +
      'case for moving it earlier, even at a lower price, gets much stronger.',
    measures: [
      '% of managed-part sales per inventory age bucket',
      'Return rate per age bucket',
      'Margin rate per age bucket',
    ],
    targets: ['Baseline first — this KPI is exploratory before it becomes a target.'],
    dataNotes: [
      'Inventory age uses the days-on-shelf value already computed on the invoice view, so no ' +
        'point-in-time inventory reconstruction is needed. Buckets match the existing DOS ladder ' +
        'used elsewhere in Nexus.',
      'Returns link back to the originating invoice line, which is what makes the correlation ' +
        'possible at all.',
    ],
    gaps: [
      'Some returns carry no source document or link to a pick ticket rather than an invoice, so ' +
        'the measured return rate slightly understates the true one. Direction of the trend is ' +
        'unaffected.',
      'The oldest bucket shows both the highest return rate and an unusually high margin rate. ' +
        'That combination is worth explaining before anyone acts on it — likely life-limited or ' +
        'repaired units priced differently.',
      MANAGED_DEFINITION_CAVEAT,
    ],
  },
];

// The Pillar 3 scorecard — the exec figure list as a single screen.
//
// Deliberately NOT a KPIS entry. The KPI pages each answer one analytical question and hang off an
// objective and an initiative; the scorecard is a rollup across both, so putting it in KPIS would
// force it into one branch of the tree and it would misrepresent itself as a tenth KPI. It sits
// directly under the pillar in the nav, matching how it is filed in the Query Commander tree.
export const SCORECARD = {
  slug: 'scorecard',
  name: 'Pillar 3 Scorecard',
  tagline: 'The figures the exec team tracks, on one screen, for any date range.',
  sections: ['Managed', 'Unmanaged', 'Buckets', 'Repairs', 'Sales'],
  queries: [
    { key: 'initiative-scorecard-managed-summary', purpose: 'The six Managed headline figures, in units sold' },
    { key: 'initiative-scorecard-managed-sold-detail', purpose: 'The exportable managed-parts-sold table' },
    { key: 'initiative-unmanaged-vs-managed-summary', purpose: 'Unmanaged revenue, units and average days on shelf' },
    { key: 'initiative-scorecard-unmanaged-onhand', purpose: 'Unmanaged units in stock (point-in-time)' },
    { key: 'initiative-margin-by-bucket', purpose: 'Margin $ and % per inventory bucket' },
    { key: 'initiative-scorecard-repair-spend', purpose: 'Actual repair spend vs cross-vendor benchmark' },
    { key: 'initiative-scorecard-sales-summary', purpose: 'Quoted vs sold, instances and dollars' },
  ],
};

export const kpisForObjective = (objectiveId) => KPIS.filter((k) => k.objectiveId === objectiveId);

export const kpiBySlug = (slug) => KPIS.find((k) => k.slug === slug);

export const STATUS_META = {
  live: { label: 'Live data', color: 'teal' },
  partial: { label: 'Partly blocked', color: 'yellow' },
  proposed: { label: 'Proposed', color: 'gray' },
};

/**
 * Builds the Pillar > Objective > Initiative > KPI tree used by the nav, matching design.md.
 *
 * Empty branches are pruned at every level, so adding a placeholder pillar or initiative before
 * any KPI references it will not leave a dead node in the menu.
 */
export function buildInitiativeTree() {
  return PILLARS.map((pillar) => ({
    ...pillar,
    objectives: OBJECTIVES.filter((o) => o.pillarId === pillar.id)
      .map((objective) => ({
        ...objective,
        initiatives: INITIATIVES.filter((i) => i.objectiveId === objective.id)
          .map((initiative) => ({
            ...initiative,
            kpis: KPIS.filter((k) => k.initiativeId === initiative.id),
          }))
          .filter((i) => i.kpis.length > 0),
      }))
      .filter((o) => o.initiatives.length > 0),
  })).filter((p) => p.objectives.length > 0);
}

/** Default reporting window: trailing 3 years, per the standing cap on context data pulls. */
export function defaultWindow() {
  const to = new Date();
  const from = new Date(to.getFullYear() - 3, to.getMonth(), to.getDate());
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(from), to: fmt(to) };
}
